import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, FolderPlus } from 'lucide-react';
import { useHierarchicalFoldersStore } from '@/store/hierarchicalFoldersStore';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface CreateFolderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId?: string | null;
  onSuccess?: (folderId: string) => void;
}

export function CreateFolderModal({ 
  open, 
  onOpenChange, 
  parentId = null,
  onSuccess 
}: CreateFolderModalProps) {
  useLanguage();
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const createFolder = useHierarchicalFoldersStore(state => state.createFolder);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para criar pastas"));
      onOpenChange(false);
    }
  }, [open, canEdit, onOpenChange]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = name.trim();

    if (!trimmed) {
      setError(translateInterfaceText("Digite um nome para a pasta"));
      return;
    }

    if (trimmed.length < 2) {
      setError(translateInterfaceText("O nome deve ter pelo menos 2 caracteres"));
      return;
    }

    if (trimmed.length > 80) {
      setError(translateInterfaceText("Nome muito longo (máximo 80 caracteres)"));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const newFolder = await createFolder(trimmed, parentId);
      
      // Reset and close
      setName('');
      setError('');
      onOpenChange(false);
      
      // Success callback - parent will handle toast
      if (onSuccess) {
        onSuccess(newFolder.id);
      }
    } catch (err) {
      console.error('❌ Error creating folder:', err);
      setError(err instanceof Error ? err.message : translateInterfaceText("Erro ao criar pasta"));
      toast.error(translateInterfaceText("Erro ao criar pasta"));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-tour="modal-folder-create"
        className="!w-[520px] max-w-[92vw] p-0 rounded-[18px] overflow-hidden border border-border shadow-[0_12px_32px_rgba(0,0,0,0.14)] backdrop-blur-[6px]"
        aria-labelledby="modal-title"
        aria-modal="true"
        role="dialog"
      >
        <DialogHeader className="px-8 pt-7 pb-5">
          <DialogTitle id="modal-title" className="text-[20px] font-semibold text-foreground flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-[#2D7CFF]" />
            {translateInterfaceText("Criar nova pasta")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="px-8 pb-3 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="folderName" className="text-sm font-medium text-foreground/80">
                {translateInterfaceText("Nome da pasta")}</Label>
              <Input
                id="folderName"
                data-tour="folder-name-input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value.slice(0, 80));
                  setError('');
                }}
                placeholder={translateInterfaceText("Ex: Funil Low Ticket / Campanha Fevereiro")}
                maxLength={80}
                autoFocus
                disabled={isLoading}
                className="h-12 rounded-xl border border-border bg-muted/50 px-4 text-[15px] placeholder:text-muted-foreground focus:border-[#2D7CFF] focus:bg-background focus:shadow-[0_0_0_2px_rgba(45,124,255,0.15)] transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit(e);
                  if (e.key === 'Escape') onOpenChange(false);
                }}
              />
              <p className="text-[13px] text-muted-foreground mt-1.5">
                {translateInterfaceText("Você poderá mover vídeos para esta pasta depois. (")}{name.length}/80)
              </p>
              {error && (
                <p className="text-xs text-red-600">{error}</p>
              )}
            </div>
          </div>
          
          <DialogFooter className="px-8 py-7 border-t border-border flex justify-end gap-3.5">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="h-11 rounded-xl border border-border bg-transparent text-muted-foreground px-[22px] hover:bg-muted"
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button
              type="submit"
              data-tour="folder-create-confirm"
              disabled={isLoading || !name.trim()}
              className="h-11 rounded-xl bg-[#2D7CFF] text-white px-[22px] font-semibold hover:bg-[#1E5FCC] transition-colors"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {translateInterfaceText("Criar pasta")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
